import { RundownToken } from '../../axios'
import { useUser } from '../../entities/user/api/hooks'
import { useTeamsByUserId } from '../../entities/team/api/hooks'

/**
 * Query guard for dashboard context.
 *
 * Returns true when it's safe to make team-specific queries:
 * - User is authenticated
 * - User's teams have loaded
 * - User is a member of the specified team
 *
 * Use this for React Query `enabled` conditions instead of manual checks.
 *
 * @example
 *   ;```ts
 *   const enabled = useTeamQueryEnabled(teamId)
 *
 *   const { data } = useQuery({
 *     queryKey: ['team-data', teamId],
 *     queryFn: () => fetchTeamData(teamId),
 *     enabled,
 *   })
 *   ```
 *
 * @param teamId - The team ID to check membership for
 *
 * @returns true if the query should be enabled
 */
export const useTeamQueryEnabled = (teamId: string | undefined): boolean => {
  const { data: user, isLoading: isUserLoading } = useUser()
  const { data: teams, isLoading: isTeamsLoading } = useTeamsByUserId({
    params: { userId: user?.uid ?? '' },
    options: { enabled: !!user?.uid },
  })

  // Still loading
  if (isUserLoading || isTeamsLoading) return false

  // No teamId provided
  if (!teamId) return false

  // No teams data yet
  if (!teams) return false

  // Check if user is a member of this team
  return teams.some((t) => t.id === teamId)
}

/**
 * Query guard for rundown context.
 *
 * Returns true when RundownToken has been initialized with permissions.
 * This happens after the token is fetched in RundownParent.
 *
 * Use this for React Query `enabled` conditions in rundown-specific queries.
 *
 * @example
 *   ;```ts
 *   const enabled = useRundownQueryEnabled()
 *
 *   const { data } = useQuery({
 *     queryKey: ['rundown-data', rundownId],
 *     queryFn: () => fetchRundownData(rundownId),
 *     enabled,
 *   })
 *   ```
 *
 * @returns true if RundownToken has valid permissions
 */
export const useRundownQueryEnabled = (): boolean => {
  return typeof RundownToken.permissions === 'number'
}
